// src/react-auth0-wrapper.js
import React, { useState, useEffect, useContext, useCallback } from 'react'
import createAuth0Client from '@auth0/auth0-spa-js'

import jwt from 'jsonwebtoken'

const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname)

export const Auth0Context = React.createContext()
export const useAuth0 = () => useContext(Auth0Context)
export const Auth0Provider = ({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  ...initOptions
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState()
  const [user, setUser] = useState()
  const [auth0Client, setAuth0] = useState()
  const [loading, setLoading] = useState(true)
  const [popupOpen, setPopupOpen] = useState(false)

  const getIdTokenClaims = useCallback(
    (...p) => auth0Client.getIdTokenClaims(...p),
    [auth0Client],
  )
  const loginWithRedirect = useCallback(
    (...p) => auth0Client.loginWithRedirect(...p),
    [auth0Client],
  )
  const getTokenSilently = useCallback(
    (...p) => auth0Client.getTokenSilently(...p),
    [auth0Client],
  )
  const getTokenWithPopup = useCallback(
    (...p) => auth0Client.getTokenWithPopup(...p),
    [auth0Client],
  )
  const logout = useCallback((...p) => auth0Client.logout(...p), [auth0Client])
  const getUserMetadata = useCallback(() => {
    return !!user
      ? user[process.env.REACT_APP_AUTH0_NAMESPACE + 'user_metadata'] || {}
      : {}
  }, [user])

  const getUserAppMetadata = useCallback(() => {
    return user
      ? user[process.env.REACT_APP_AUTH0_NAMESPACE + 'app_metadata'] || {}
      : {}
  }, [user])

  useEffect(() => {
    const initAuth0 = async () => {
      const auth0FromHook = await createAuth0Client(initOptions)
      setAuth0(auth0FromHook)

      if (window.location.search.includes('code=')) {
        console.log('Not entering here')
        const { appState } = await auth0FromHook.handleRedirectCallback()
        onRedirectCallback(appState)
      }

      const isAuthenticated = await auth0FromHook.isAuthenticated()

      setIsAuthenticated(isAuthenticated)

      if (isAuthenticated) {
        const user = await auth0FromHook.getUser()

        const token = await auth0FromHook.getTokenSilently()

        const decoded = jwt.decode(token)
        setUser({
          ...user,
          permissions: decoded.permissions,
        })
      }

      setLoading(false)
    }
    initAuth0()
    // eslint-disable-next-line
  }, [])

  const loginWithPopup = async (params = {}) => {
    setPopupOpen(true)
    try {
      await auth0Client.loginWithPopup(params)
    } catch (error) {
      console.error(error)
    } finally {
      setPopupOpen(false)
    }
    const user = await auth0Client.getUser()
    setUser(user)
    setIsAuthenticated(true)
  }

  const handleRedirectCallback = async () => {
    setLoading(true)
    await auth0Client.handleRedirectCallback()
    const user = await auth0Client.getUser()
    setLoading(false)
    setIsAuthenticated(true)
    setUser(user)
  }

  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        popupOpen,
        loginWithPopup,
        handleRedirectCallback,
        getIdTokenClaims,
        loginWithRedirect,
        getTokenSilently,
        getTokenWithPopup,
        logout,
        getUserMetadata,
        getUserAppMetadata,
      }}
    >
      {children}
    </Auth0Context.Provider>
  )
}
